import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { get } from './api';

interface ReadyRoom {
  roomId: string;
  roomNumber: string;
  at: string;
  guestName: string | null;
  eta: string | null;
  vipCode: string | null;
  rushed: boolean;
}

/** A short two-note chime, generated (no sound file). Browsers allow it once someone has clicked on the page. */
function chime() {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [880, 1320].forEach((freq, i) => {
      const at = ctx.currentTime + i * 0.18;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.15, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.3);
      osc.connect(gain).connect(ctx.destination);
      osc.start(at);
      osc.stop(at + 0.32);
    });
    setTimeout(() => void ctx.close(), 1000);
  } catch {
    // Sound is a nicety; the pop-up is what matters.
  }
}

/**
 * Front desk: a pop-up (and chime) the moment housekeeping releases a room that an
 * arriving guest, or a rush request, is waiting for. No more phoning the floor.
 */
export function useRoomReadyAlerts(enabled: boolean) {
  const since = useRef<string | null>(null);
  const seen = useRef(new Set<string>());
  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    async function poll() {
      try {
        const r = await get<{ since: string; rooms: ReadyRoom[] }>('/housekeeping/ready', since.current ? { since: since.current } : undefined);
        if (stopped) return;
        since.current = r.since;
        // The server repeats the last few seconds so nothing slips between two polls; show each room once.
        const fresh = r.rooms.filter((x) => !seen.current.has(`${x.roomId}@${x.at}`));
        for (const x of fresh) {
          seen.current.add(`${x.roomId}@${x.at}`);
          toast.success(`Room ${x.roomNumber} is ready`, {
            description: x.guestName
              ? `For ${x.guestName}${x.eta ? ` · arriving ${x.eta}` : ''}${x.vipCode ? ` · VIP ${x.vipCode}` : ''}${x.rushed ? ' · rushed' : ''}`
              : 'Housekeeping has released the room you rushed.',
            duration: 20_000,
          });
        }
        if (fresh.length) chime();
      } catch {
        // Offline or signed out: try again on the next round.
      }
    }
    void poll();
    const timer = setInterval(() => void poll(), 15_000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [enabled]);
}
